<template>
  <v-card
      class="metric-card"
      :class="[cardVariant, { 'card-animating': isAnimating, 'card-loading': loading }]"
      elevation="2"
      @click="!loading && $emit('detail-click')"
  >
    <!-- Top Border Indicator -->
    <div class="card-border-top" :class="borderVariant"></div>

    <!-- Loading Overlay -->
    <transition name="fade">
      <div v-if="loading" class="loading-overlay">
        <div class="loading-shimmer"></div>
      </div>
    </transition>

    <!-- Card Content -->
    <v-card-text class="card-content pa-6">
      <!-- Header -->
      <div class="metric-header">
        <div v-if="loading" class="skeleton-wrapper">
          <div class="skeleton skeleton-icon"></div>
        </div>
        <div v-else class="icon-wrapper" :class="iconVariant">
          <v-icon :color="getIconColor()" size="28">{{ icon }}</v-icon>
        </div>

        <div v-if="loading" class="skeleton skeleton-chip"></div>
        <v-chip
            v-else-if="trend"
            small
            :class="trendChipClass"
            class="trend-chip"
        >
          <v-icon left x-small>{{ getTrendIcon() }}</v-icon>
          {{ trend }}
        </v-chip>
      </div>

      <!-- Body -->
      <div class="metric-body">
        <div v-if="loading" class="skeleton skeleton-title"></div>
        <div v-else class="metric-title">{{ title }}</div>

        <div v-if="loading" class="skeleton skeleton-value"></div>
        <div v-else class="metric-value text-truncate" :class="[{ 'value-animating': isAnimating }, valueColorClass]">
          {{ formattedValue }}
        </div>

        <div v-if="loading" class="skeleton skeleton-subtitle"></div>
        <div v-else-if="subtitle" class="metric-subtitle">{{ subtitle }}</div>
      </div>

      <!-- Footer -->
      <div v-if="showFooter" class="metric-footer">
        <div v-if="loading" class="footer-loading">
          <div class="skeleton skeleton-footer-text"></div>
          <div class="skeleton skeleton-footer-btn"></div>
        </div>
        <template v-else>
          <div class="footer-info">
            <v-icon x-small color="#64748b" class="mr-1">mdi-chart-line</v-icon>
            <span>{{ getFooterText() }}</span>
          </div>
          <v-btn
              x-small
              text
              :color="getIconColor()"
              class="detail-btn"
          >
            Ver detalhes
            <v-icon right x-small>mdi-chevron-right</v-icon>
          </v-btn>
        </template>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'MetricCard',

  props: {
    title: {
      type: String,
      required: true
    },
    value: {
      type: [String, Number],
      required: true
    },
    icon: {
      type: String,
      default: 'mdi-chart-box-outline'
    },
    trend: {
      type: String,
      default: ''
    },
    trendType: {
      type: String,
      default: 'positive',
      validator: (value) => ['positive', 'negative', 'neutral', 'good', 'attention'].includes(value)
    },
    subtitle: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'primary', 'success', 'warning', 'danger'].includes(value)
    },
    valueColor: {
      type: String,
      default: '',
      validator: (value) => ['', 'attention', 'success', 'warning'].includes(value)
    },
    showFooter: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isAnimating: false,
    };
  },

  computed: {
    cardVariant() {
      return `variant-${this.variant}`;
    },

    iconVariant() {
      return `icon-${this.variant}`;
    },

    borderVariant() {
      return `border-${this.variant}`;
    },

    trendChipClass() {
      return `trend-${this.trendType}`;
    },

    valueColorClass() {
      if (this.valueColor === 'attention') return 'value-attention';
      if (this.valueColor === 'success') return 'value-success';
      if (this.valueColor === 'warning') return 'value-warning';
      return '';
    },

    formattedValue() {
      if (typeof this.value === 'number') {
        return this.value.toLocaleString('pt-BR');
      }
      return this.value;
    },
  },

  mounted() {
    if (!this.loading) {
      this.animateValue();
    }
  },

  watch: {
    value() {
      if (!this.loading) {
        this.animateValue();
      }
    },
    loading(newVal) {
      if (!newVal) {
        this.animateValue();
      }
    }
  },

  methods: {
    getTrendIcon() {
      const icons = {
        positive: 'mdi-trending-up',
        negative: 'mdi-trending-down',
        neutral: 'mdi-trending-neutral',
        good: 'mdi-check-circle',
        attention: 'mdi-alert-circle'
      };
      return icons[this.trendType] || icons.neutral;
    },

    getFooterText() {
      const texts = {
        positive: 'Crescimento no período',
        negative: 'Redução no período',
        neutral: 'Estável no período',
        good: 'Dentro do esperado',
        attention: 'Requer atenção'
      };
      return texts[this.trendType] || texts.neutral;
    },

    getIconColor() {
      const colors = {
        default: '#023449',
        primary: '#023449',
        success: '#2e7d32',
        warning: '#f57c00',
        danger: '#c62828'
      };
      return colors[this.variant] || colors.default;
    },

    animateValue() {
      this.isAnimating = true;
      setTimeout(() => {
        this.isAnimating = false;
      }, 600);
    }
  }
};
</script>

<style scoped>
.metric-card {
  position: relative;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: 1px solid #e2e8f0;
  height: 100%;
}

.metric-card:hover:not(.card-loading) {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(2, 52, 73, 0.12) !important;
  border-color: #cbd5e1;
}

.metric-card.card-loading {
  cursor: default;
  pointer-events: none;
}

.metric-card.card-animating {
  animation: cardPulse 0.6s ease;
}

@keyframes cardPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.5);
  z-index: 3;
  pointer-events: none;
}

.loading-shimmer {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.8) 50%,
      transparent 100%
  );
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

/* Skeleton Styles */
.skeleton {
  background: linear-gradient(
      90deg,
      #f0f0f0 25%,
      #e8e8e8 50%,
      #f0f0f0 75%
  );
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;
  border-radius: 8px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-wrapper {
  width: 56px;
  height: 56px;
}

.skeleton-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
}

.skeleton-chip {
  width: 80px;
  height: 24px;
  border-radius: 12px;
}

.skeleton-title {
  width: 60%;
  height: 14px;
  margin-bottom: 8px;
}

.skeleton-value {
  width: 80%;
  height: 40px;
  margin-bottom: 8px;
}

.skeleton-subtitle {
  width: 50%;
  height: 12px;
  margin-top: 4px;
}

.footer-loading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.skeleton-footer-text {
  width: 120px;
  height: 16px;
}

.skeleton-footer-btn {
  width: 90px;
  height: 24px;
  border-radius: 4px;
}

/* Top Border */
.card-border-top {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: #023449;
}

.border-primary { background: #023449; }
.border-success { background: #2e7d32; }
.border-warning { background: #f57c00; }
.border-danger { background: #c62828; }

/* Card Content */
.card-content {
  position: relative;
  z-index: 1;
}

/* Header */
.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.icon-wrapper {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(2, 52, 73, 0.08);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.metric-card:hover:not(.card-loading) .icon-wrapper {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(2, 52, 73, 0.15);
}

.icon-default { background: rgba(2, 52, 73, 0.08); }
.icon-primary { background: rgba(2, 52, 73, 0.08); }
.icon-success { background: rgba(46, 125, 50, 0.08); }
.icon-warning { background: rgba(245, 124, 0, 0.08); }
.icon-danger { background: rgba(198, 40, 40, 0.08); }

/* Trend Chip */
.trend-chip {
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 0.3px;
  border: 1px solid;
  animation: fadeInDown 0.4s ease;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.trend-positive {
  background-color: #e8f5e9 !important;
  color: #2e7d32 !important;
  border-color: #a5d6a7 !important;
}

.trend-negative {
  background-color: #ffebee !important;
  color: #c62828 !important;
  border-color: #ef9a9a !important;
}

.trend-neutral {
  background-color: #f5f5f5 !important;
  color: #64748b !important;
  border-color: #e0e0e0 !important;
}

.trend-good {
  background-color: #e8f5e9 !important;
  color: #2e7d32 !important;
  border-color: #a5d6a7 !important;
}

.trend-attention {
  background-color: #ffebee !important;
  color: #c62828 !important;
  border-color: #ef9a9a !important;
}

/* Body */
.metric-body {
  margin-bottom: 16px;
}

.metric-title {
  color: #64748b;
  font-size: 0.813rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.metric-value {
  font-size: 2.15rem;
  font-weight: 800;
  color: #023449;
  line-height: 1.2;
  letter-spacing: -0.02em;
  transition: all 0.3s ease;
}

.metric-value.value-animating {
  animation: valueScale 0.6s ease;
}

@keyframes valueScale {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

/* Value Color Classes */
.metric-value.value-attention {
  color: #c62828 !important;
}

.metric-value.value-success {
  color: #2e7d32 !important;
}

.metric-value.value-warning {
  color: #f57c00 !important;
}

.metric-subtitle {
  font-size: 0.75rem;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 4px;
}

@keyframes drawLine {
  to {
    stroke-dashoffset: 0;
  }
}

/* Footer */
.metric-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
  margin-top: 12px;
}

.footer-info {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

.detail-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  font-size: 0.75rem;
  padding: 0 8px !important;
  min-width: auto;
}

/* Variant Styles */
.variant-primary .metric-value:not(.value-attention):not(.value-success):not(.value-warning) { color: #023449; }
.variant-success .metric-value:not(.value-attention):not(.value-success):not(.value-warning) { color: #2e7d32; }
.variant-warning .metric-value:not(.value-attention):not(.value-success):not(.value-warning) { color: #f57c00; }
.variant-danger .metric-value:not(.value-attention):not(.value-success):not(.value-warning) { color: #c62828; }

/* Responsive */
@media (max-width: 960px) {
  .card-content {
    padding: 20px !important;
  }

  .icon-wrapper, .skeleton-icon, .skeleton-wrapper {
    width: 48px;
    height: 48px;
  }

  .metric-value {
    font-size: 2rem;
  }

  .skeleton-value {
    height: 36px;
  }
}

@media (max-width: 600px) {
  .metric-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .metric-value {
    font-size: 1.75rem;
  }

  .skeleton-value {
    height: 32px;
  }

  .metric-footer {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  .footer-loading {
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  .detail-btn {
    width: 100%;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .metric-card,
  .icon-wrapper,
  .metric-value,
  .trend-chip,
  .skeleton,
  .loading-shimmer {
    animation: none !important;
    transition: none !important;
  }
}
</style>